<script setup lang="ts">
import { RouterLink } from 'vue-router';
import Avatar from '../ui/Avatar.vue';
import Button from '../ui/Button.vue';
import Icon from '../ui/Icon.vue';
import Wordmark from './Wordmark.vue';
import { navItems } from './navigation';
import { useSession } from '../../stores/session';
import { useParty } from '../../stores/party';

const session = useSession();
const party = useParty();

async function signOut() {
  await session.signOut();
  party.clear();
}

const linkClass = 'flex h-11 shrink-0 items-center border-b-2 border-transparent px-3 text-[13px] font-medium text-darker transition-colors duration-150 hover:text-default lg:h-14';
const activeClass = '!border-accent !text-default';
</script>

<template>
  <header class="sticky top-0 z-30 border-b border-app bg-app">
    <div class="mx-auto flex h-14 max-w-screen-2xl items-center gap-7 px-4 lg:px-6">
      <div class="min-w-0 flex-1 lg:flex-none"><Wordmark /></div>

      <nav class="hidden h-14 min-w-0 flex-1 items-stretch lg:flex" aria-label="Site sections">
        <RouterLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :class="linkClass"
          :active-class="item.to === '/' ? '' : activeClass"
          :exact-active-class="item.to === '/' ? activeClass : ''"
        >
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="flex shrink-0 items-center gap-2">
        <p v-if="session.status.value === 'unreachable'" class="flex items-center gap-1.5 text-[12px] text-accent" role="status">
          <Icon name="alert" class="size-3.5" />
          <span class="hidden sm:inline">API offline</span>
        </p>

        <template v-if="session.status.value === 'signed-in' && session.user.value">
          <RouterLink
            to="/me"
            class="flex items-center gap-2 rounded-md border border-transparent py-1 pl-1 pr-2 transition-colors duration-150 hover:border-card hover:bg-raised"
          >
            <Avatar :src="session.user.value.avatarUrl" :name="session.user.value.displayName" />
            <span class="hidden max-w-[16ch] truncate text-[13px] font-medium text-dark sm:inline">
              {{ session.user.value.displayName }}
            </span>
          </RouterLink>
          <Button intent="ghost" size="sm" aria-label="Sign out" @click="signOut">
            <template #icon><Icon name="logout" class="size-4" /></template>
          </Button>
        </template>

        <Button
          v-else-if="session.isResolved.value"
          intent="discord"
          size="sm"
          @click="session.signIn()"
        >
          <template #icon><Icon name="discord" class="size-4" /></template>
          <span class="hidden sm:inline">Sign in with Discord</span>
          <span class="sm:hidden">Sign in</span>
        </Button>

        <div v-else class="h-8 w-28 rounded-md bg-raised" aria-hidden="true" />
      </div>
    </div>
    <nav class="flex h-11 items-stretch overflow-x-auto border-t border-app px-4 lg:hidden" aria-label="Site sections">
      <RouterLink
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        :class="linkClass"
        :active-class="item.to === '/' ? '' : activeClass"
        :exact-active-class="item.to === '/' ? activeClass : ''"
      >
        {{ item.label }}
      </RouterLink>
    </nav>
  </header>
</template>
